import { CONTEXT_PLACEHOLDER_NAMES } from "./placeholders";

export type FormSegmentType =
  | "text"
  | "context"
  | "input"
  | "textarea"
  | "break";

export interface IFormSegment {
  type: FormSegmentType;
  /** For "text": the literal text. For "context": the placeholder name. */
  value: string;
  /** Zero-based index across all input/textarea fields, in document order. */
  inputIndex?: number;
}

const TOKEN_REGEX = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;

/**
 * Splits a form template into ordered segments. Unknown tokens are treated as
 * plain text so nothing is silently dropped. Input/textarea segments carry a
 * sequential `inputIndex` used to line answers up with fields.
 */
export const parseFormTemplate = (template: string): IFormSegment[] => {
  const segments: IFormSegment[] = [];

  if (!template) {
    return segments;
  }

  let lastIndex = 0;
  let inputIndex = 0;
  let match: RegExpExecArray | null;

  TOKEN_REGEX.lastIndex = 0;

  while ((match = TOKEN_REGEX.exec(template)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        value: template.slice(lastIndex, match.index),
      });
    }

    const name = match[1];

    if (name === "input") {
      segments.push({ type: "input", value: "", inputIndex: inputIndex++ });
    } else if (name === "textarea") {
      segments.push({ type: "textarea", value: "", inputIndex: inputIndex++ });
    } else if (name === "break") {
      segments.push({ type: "break", value: "" });
    } else if (CONTEXT_PLACEHOLDER_NAMES.has(name)) {
      segments.push({ type: "context", value: name });
    } else {
      // Unknown token — keep it verbatim as text.
      segments.push({ type: "text", value: match[0] });
    }

    lastIndex = TOKEN_REGEX.lastIndex;
  }

  if (lastIndex < template.length) {
    segments.push({ type: "text", value: template.slice(lastIndex) });
  }

  return segments;
};

/** Number of required input/textarea fields in a template. */
export const countFormInputs = (template: string): number =>
  parseFormTemplate(template).filter(
    (s) => s.type === "input" || s.type === "textarea",
  ).length;
